import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context.jsx';
import { PAINTINGS } from '../data/content.js';
import { WireSphere, PaintingBreath } from '../components/canvas.jsx';
import { MagBtn, SplitTitle, FunnelCard, FaqItem, TiltCard } from '../components/ui.jsx';
import { deriveArtworkSlug } from '../lib/slug.js';
import Seo from '../components/Seo.jsx';

export default function Home() {
  const { lang, t, li, CH, CL, CV } = useApp();
  const nav = useNavigate();
  const [vis, setVis] = useState(new Set());
  const [openFaq, setOpenFaq] = useState(0);
  const [activeTest, setActiveTest] = useState(0);
  const tmHover = useRef(false);
  const tmTouchRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => {
      if (!tmHover.current) setActiveTest((p) => (p + 1) % t.testimonials.items.length);
    }, 6000);
    return () => clearInterval(id);
  }, [t.testimonials.items.length]);
  const heroGhostRef = useRef(null);
  const heroContentRef = useRef(null);
  const [hp, setHp] = useState(0);
  const reducedMotion = useRef(false);

  useEffect(() => {
    reducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    const timers = [
      setTimeout(() => setHp(2), 200),
      setTimeout(() => setHp(3), 400),
      setTimeout(() => setHp(4), 700),
      setTimeout(() => setHp(5), 1000),
      setTimeout(() => setHp(6), 1300),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (en) => en.forEach((e) => { if (e.isIntersecting) setVis((p) => new Set([...p, e.target.id])); }),
      { threshold: 0.1, rootMargin: '-50px' }
    );
    document.querySelectorAll('section[id]').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (reducedMotion.current) return;
    const onMouse = (e) => {
      const mx = e.clientX / window.innerWidth - 0.5;
      const my = e.clientY / window.innerHeight - 0.5;
      if (heroGhostRef.current) heroGhostRef.current.style.transform = `translate(calc(-50% + ${mx * -30}px), calc(-50% + ${my * -20}px))`;
      if (heroContentRef.current) heroContentRef.current.style.transform = `translate(${mx * -12}px, ${my * -8}px)`;
    };
    window.addEventListener('mousemove', onMouse);
    return () => window.removeEventListener('mousemove', onMouse);
  }, []);

  // featured works — первые 8 in-stock (приоритет: с ценой)
  const featured = PAINTINGS.filter((p) => p.tech !== 'prints').slice(0, 8);

  return (
    <>
      <Seo
        title={lang === 'ru' ? 'Интуитивная живопись · картины из будущего' : 'Intuitive painting · pictures from the future'}
        description={lang === 'ru'
          ? 'Авторская интуитивная живопись Елизаветы Федькиной — живые картины для интерьера, мастер-классы и индивидуальные работы в Москве.'
          : 'Authentic intuitive painting by Elizaveta Fedkina — living artworks for interiors, workshops and custom pieces. Moscow.'}
        path="/"
      />
      <section id="hero" className="H">
        <div className="H-canvas"><WireSphere /></div>
        <div className="H-gradient" /><div className="H-vignette" />
        <div ref={heroGhostRef} className="H-ghost" style={{ transform: 'translate(-50%, -50%)' }}>{lang === 'ru' ? 'Федькина' : 'Fedkina'}</div>
        <div className="H-line" style={{ top: '30%', left: '5%', width: '15%', opacity: hp >= 2 ? 0.15 : 0, transition: 'opacity 1.5s' }} />
        <div className="H-line" style={{ bottom: '35%', right: '5%', width: '12%', opacity: hp >= 3 ? 0.12 : 0, transition: 'opacity 1.5s' }} />
        <div className={`H-float H-float--1 ${hp >= 5 ? 'in' : ''}`}><PaintingBreath src="/paintings/ikhtis.webp" intensity={0.3} /></div>
        <div className={`H-float H-float--2 ${hp >= 5 ? 'in' : ''}`}><PaintingBreath src="/paintings/zhar-ptitsa.webp" intensity={0.3} /></div>
        <div ref={heroContentRef} className="H-content" style={{ transition: 'transform .3s ease-out' }}>
          <div className={`H-name ${hp >= 2 ? 'in' : ''}`}>{lang === 'ru' ? 'Елизавета' : 'Elizaveta'}</div>
          <div className={`H-surname ${hp >= 3 ? 'in' : ''}`}>{lang === 'ru' ? 'Федькина' : 'Fedkina'}</div>
          <p className={`H-sub ${hp >= 4 ? 'in' : ''}`}>{t.hero.sub}</p>
          <p className={`H-lead ${hp >= 4 ? 'in' : ''}`}>{t.hero.lead}</p>
          <div className={`H-btns H-btns--3 ${hp >= 5 ? 'in' : ''}`}>
            <MagBtn className="B1" onClick={() => nav('/artworks')}   onMouseEnter={CH} onMouseLeave={CL}>{t.hero.cta}</MagBtn>
            <MagBtn className="B2" onClick={() => nav('/workshops')}  onMouseEnter={CH} onMouseLeave={CL}>{t.hero.cta2}</MagBtn>
            <MagBtn className="B2" onClick={() => nav('/custom')}     onMouseEnter={CH} onMouseLeave={CL}>{t.hero.cta3}</MagBtn>
          </div>
        </div>
        <div className={`H-badge ${hp >= 6 ? 'in' : ''}`} style={{ top: '18%', right: '12%' }}>{lang === 'ru' ? 'Акрил · Смешанная техника' : 'Acrylic · Mixed media'}</div>
        <div className={`H-badge ${hp >= 6 ? 'in' : ''}`} style={{ bottom: '22%', left: '8%' }}>{lang === 'ru' ? 'Москва' : 'Moscow'}</div>
        <div className="H-scroll"><div className="H-scroll-text">Scroll</div><div className="H-scroll-line" /></div>
      </section>

      <div className="MQ"><div className="MQ-inner"><span className="MQ-text">{t.marquee.repeat(4)}</span></div></div>

      {/* ═══ 4 направления ═══ */}
      <section id="funnels" className={`S ${vis.has('funnels') ? 'v' : ''}`}>
        <div className="S-tag">{t.funnels.tag}</div>
        <h2 className="S-title"><SplitTitle text={t.funnels.title} accent={t.funnels.accent} visible={vis.has('funnels')} /></h2>
        <div className="S-sub">{t.funnels.sub}</div>
        <div className="FN-grid">
          {t.funnels.items.map((f, i) => (
            <FunnelCard
              key={f.key}
              to={f.to}
              index={i}
              title={f.t}
              desc={f.d}
              cta={lang === 'ru' ? 'Подробнее' : 'Learn more'}
              onMouseEnter={CH}
              onMouseLeave={CL}
            />
          ))}
        </div>
      </section>

      <div className="divider"><svg viewBox="0 0 1200 12" preserveAspectRatio="none"><path d="M0,6 C150,2 300,10 450,5 C600,0 750,11 900,6 C1000,3 1100,8 1200,6" stroke="rgba(196,148,74,0.08)" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg></div>

      {/* ═══ Избранные работы ═══ */}
      <section id="featured" className={`S ${vis.has('featured') ? 'v' : ''}`}>
        <div className="S-tag">{t.featured.tag}</div>
        <h2 className="S-title"><SplitTitle text={t.featured.title} accent={t.featured.accent} visible={vis.has('featured')} /></h2>
        <div className="FE-grid">
          {featured.map((p, i) => {
            const slug = deriveArtworkSlug(p);
            const isSold = p.price === 'Продано' || p.price === 'Продана';
            return (
              <Link
                key={slug}
                to={`/artworks/${slug}`}
                className="FE-card"
                onMouseEnter={CV}
                onMouseLeave={CL}
              >
                <div className="FE-img-wrap">
                  {p.img ? (
                    <img className="FE-img" src={p.img} alt={p.title[li]} loading="lazy" />
                  ) : (
                    <div className="FE-img" style={{ background: p.gradient }} />
                  )}
                  {isSold && <div className="FE-sold">{lang === 'ru' ? 'Продано' : 'Sold'}</div>}
                </div>
                <div className="FE-body">
                  <div className="FE-t">{p.title[li]}</div>
                  <div className="FE-m">{p.size} {lang === 'ru' ? 'см' : 'cm'} · {p.year}</div>
                  {p.price && !isSold && <div className="FE-p">{p.price}</div>}
                </div>
              </Link>
            );
          })}
        </div>
        <div className="FE-more">
          <Link to="/artworks" className="B2" onMouseEnter={CH} onMouseLeave={CL}>{t.featured.viewAll}</Link>
        </div>
      </section>

      <div className="divider"><svg viewBox="0 0 1200 12" preserveAspectRatio="none"><path d="M0,6 C150,2 300,10 450,5 C600,0 750,11 900,6 C1000,3 1100,8 1200,6" stroke="rgba(196,148,74,0.08)" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg></div>

      {/* ═══ Услуги ═══ */}
      <section id="services" className={`S ${vis.has('services') ? 'v' : ''}`}>
        <div className="S-tag">{t.services.tag}</div>
        <h2 className="S-title S-title--ink"><SplitTitle text={t.services.title} accent={t.services.accent} visible={vis.has('services')} /></h2>
        <div className="SV-grid">
          {t.services.items.map((s, i) => (
            <TiltCard key={i} className="SV-card"
              onMouseEnter={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                e.currentTarget.style.setProperty('--drip-x', ((e.clientX - r.left) / r.width * 100) + '%');
                e.currentTarget.classList.add('dripping');
              }}
              onMouseLeave={(e) => { e.currentTarget.classList.remove('dripping'); }}
            >
              <div className="SV-num">{s.i}</div>
              <div className="SV-name">{s.t}</div>
              <div className="SV-desc">{s.d}</div>
              <div className="SV-price">{s.p}</div>
            </TiltCard>
          ))}
        </div>
      </section>

      <div className="divider"><svg viewBox="0 0 1200 12" preserveAspectRatio="none"><path d="M0,6 C150,2 300,10 450,5 C600,0 750,11 900,6 C1000,3 1100,8 1200,6" stroke="rgba(196,148,74,0.08)" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg></div>

      {/* ═══ Отзывы ═══ */}
      <section id="testimonials" className={`S ${vis.has('testimonials') ? 'v' : ''}`}>
        <div className="S-tag">{t.testimonials.tag}</div>
        <h2 className="S-title S-title--ink"><SplitTitle text={t.testimonials.title} accent={t.testimonials.accent} visible={vis.has('testimonials')} /></h2>
        <div className="TM-quote-mark">«</div>
        <div className="TM-wrap"
          onMouseEnter={() => { tmHover.current = true; }}
          onMouseLeave={() => { tmHover.current = false; }}
          onTouchStart={(e) => { tmTouchRef.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (tmTouchRef.current === null) return;
            const d = e.changedTouches[0].clientX - tmTouchRef.current;
            if (Math.abs(d) > 50) {
              d > 0
                ? setActiveTest((p) => p > 0 ? p - 1 : t.testimonials.items.length - 1)
                : setActiveTest((p) => (p + 1) % t.testimonials.items.length);
            }
            tmTouchRef.current = null;
          }}>
          {t.testimonials.items.map((item, i) => (
            <div key={i} className={`TM-card ${activeTest === i ? 'on' : ''}`}>
              <div className="TM-text">«{item.text}»</div>
              <div className="TM-author">{item.author}</div>
              <div className="TM-role">{item.role}</div>
            </div>
          ))}
        </div>
        <div className="TM-dots">
          {t.testimonials.items.map((_, i) => (
            <button key={i} className={`TM-dot ${activeTest === i ? 'on' : ''}`} onClick={() => setActiveTest(i)} onMouseEnter={CH} onMouseLeave={CL}>
              {activeTest === i && <span className={`TM-prog ${tmHover.current ? 'paused' : ''}`} key={activeTest} />}
            </button>
          ))}
        </div>
      </section>

      <div className="divider"><svg viewBox="0 0 1200 12" preserveAspectRatio="none"><path d="M0,6 C150,2 300,10 450,5 C600,0 750,11 900,6 C1000,3 1100,8 1200,6" stroke="rgba(196,148,74,0.08)" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg></div>

      {/* ═══ FAQ ═══ */}
      <section id="faq" className={`S ${vis.has('faq') ? 'v' : ''}`}>
        <div className="S-tag">{t.faq.tag}</div>
        <h2 className="S-title"><SplitTitle text={t.faq.title} accent={t.faq.accent} visible={vis.has('faq')} /></h2>
        <div className="FAQ-list">
          {t.faq.items.map((it, i) => (
            <FaqItem
              key={i}
              q={it.q}
              a={it.a}
              open={openFaq === i}
              onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
            />
          ))}
        </div>
      </section>

      <div className="divider"><svg viewBox="0 0 1200 12" preserveAspectRatio="none"><path d="M0,6 C150,2 300,10 450,5 C600,0 750,11 900,6 C1000,3 1100,8 1200,6" stroke="rgba(196,148,74,0.08)" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg></div>

      {/* ═══ финальный CTA ═══ */}
      <section id="final-cta" className={`S ${vis.has('final-cta') ? 'v' : ''}`}>
        <h2 className="S-title"><SplitTitle text={t.finalCta.title} accent="" visible={vis.has('final-cta')} /></h2>
        <div className="FC-grid">
          {t.finalCta.items.map((it, i) => (
            <Link key={i} to={it.to} className="FC-card" onMouseEnter={CH} onMouseLeave={CL}>
              <div className="FC-t">{it.t}</div>
              <div className="FC-arrow">→</div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
